// Base Camp API klijent — isti fetch-wrapper obrazac kao front-compass/src/utils/api.js.
// Razlika: Base Camp ima jedan tip sesije (person-oriented, rola je unija u tokenu),
// pa je token jedan ključ umesto po-roli.
export const BASE = process.env.REACT_APP_API_URL || 'https://api.hansenbeck.com';

const TOKEN_KEY = 'basecamp_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('basecamp_user');
}

export function setUser(user) {
  localStorage.setItem('basecamp_user', JSON.stringify(user));
}

export function getUser() {
  try { return JSON.parse(localStorage.getItem('basecamp_user')); }
  catch { return null; }
}

async function request(path, options = {}, auth = true) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (auth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (res.status === 401) {
    if (auth) {
      clearToken();
      window.location.href = '/login';
      return;
    }
    throw new Error('Invalid credentials');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || `HTTP ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// Member photos (Graph proxy) — cached so lists don't refetch on every render/visit.
const _photoCache = new Map();
async function fetchMemberPhoto(id) {
  try {
    const token = getToken();
    const res = await fetch(`${BASE}/api/basecamp/members/${id}/photo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok || res.status === 204) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export const api = {
  // Auth — Microsoft Entra SSO (idToken iz MSAL-a razmenjujemo za basecamp JWT)
  loginMicrosoft: (idToken) => request('/api/basecamp/auth/microsoft', { method: 'POST', body: JSON.stringify({ idToken }) }, false),
  getMe: () => request('/api/basecamp/auth/me'),

  // Admin — upravljanje korisnicima (samo SUPERADMIN)
  admin: {
    getUsers: () => request('/api/basecamp/admin/users'),
    updateRoles: (id, roles) => request(`/api/basecamp/admin/users/${id}/roles`, { method: 'PUT', body: JSON.stringify({ roles }) }),
    updateFuncs: (id, funcs) => request(`/api/basecamp/admin/users/${id}/funcs`, { method: 'PUT', body: JSON.stringify({ funcs }) }),
    setActive: (id, isActive) => request(`/api/basecamp/admin/users/${id}/active`, { method: 'PUT', body: JSON.stringify({ isActive }) }),
    // Član Basecamp-a da/ne — flag u našoj bazi; Microsoft nalog se ne dira.
    setMembership: (id, isMember) => request(`/api/basecamp/admin/users/${id}/membership`, { method: 'PUT', body: JSON.stringify({ isMember }) }),
    setTarget: (id, pipelineTarget) => request(`/api/basecamp/admin/users/${id}/target`, { method: 'PUT', body: JSON.stringify({ pipelineTarget }) }),
    deleteUser: (id) => request(`/api/basecamp/admin/users/${id}`, { method: 'DELETE' }),
    updateProfile: (id, data) => request(`/api/basecamp/admin/users/${id}/profile`, { method: 'PUT', body: JSON.stringify(data) }),
    // CV fajl — multipart upload (browser sam postavlja Content-Type sa boundary-jem).
    uploadCv: async (id, file) => {
      const token = getToken();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/api/basecamp/admin/users/${id}/cv`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({ message: res.statusText })); throw new Error(e.message || `HTTP ${res.status}`); }
      return res.json();
    },
    deleteCv: (id) => request(`/api/basecamp/admin/users/${id}/cv`, { method: 'DELETE' }),
    syncUsers: () => request('/api/basecamp/admin/sync-users', { method: 'POST' }),
    // Povuci artefakt fajlove iz OneDrive-a (Graph) — može trajati; vraća brojače.
    // Opcioni `code` → sync samo za jedan Unit (npr. 'IE009').
    syncMaterials: (code) => request('/api/basecamp/admin/sync-materials', { method: 'POST', body: JSON.stringify(code ? { code } : {}) }),
    // Kreiranje / izmena Unita (superadmin) + vraćanje pod kontrolu registrija.
    createUnit: (data) => request('/api/basecamp/admin/units', { method: 'POST', body: JSON.stringify(data) }),
    updateUnit: (code, data) => request(`/api/basecamp/admin/units/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(data) }),
    revertUnit: (code) => request(`/api/basecamp/admin/units/${encodeURIComponent(code)}/revert`, { method: 'POST' }),
  },

  // Deliver biblioteka
  getUnits: () => request('/api/basecamp/units'),
  getUnit: (code) => request(`/api/basecamp/units/${encodeURIComponent(code)}`),
  // Artefakt fajl — endpoint traži Bearer token → blob → objectURL (za pregled) ili download.
  getArtifactFileURL: async (id) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/basecamp/artifacts/${id}/file`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok || res.status === 204) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return URL.createObjectURL(blob);
  },
  // Word (.docx) → readable HTML for the in-browser viewer.
  getArtifactPreview: (id) => request(`/api/basecamp/artifacts/${id}/preview`),
  downloadArtifact: async (id, filename) => {
    const url = await api.getArtifactFileURL(id);
    if (!url) throw new Error('File not available yet');
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'file';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  },
  getProgrammes: () => request('/api/basecamp/programmes'),
  getProgramme: (id) => request(`/api/basecamp/programmes/${id}`),
  // W2 — catalogue Measure · Develop · Customise
  getCatalogue: () => request('/api/basecamp/catalogue'),
  getCatalogueProgramme: (slug) => request(`/api/basecamp/catalogue/${encodeURIComponent(slug)}`),
  getLogs: () => request('/api/basecamp/logs'),
  getFacets: (profile) => request(`/api/basecamp/facets${profile ? `?profile=${encodeURIComponent(profile)}` : ''}`),
  getArtefacts: () => request('/api/basecamp/artefacts'),

  // Grow — zahtev za self/360 procenu (šalje generisani email zaduženoj osobi)
  requestAssessment: (type) => request('/api/basecamp/grow/assessment-request', { method: 'POST', body: JSON.stringify({ type }) }),

  // Content proposals (Open Renovation)
  proposeContent: async ({ title, description, area, file }) => {
    const token = getToken();
    const fd = new FormData();
    fd.append('title', title);
    if (description) fd.append('description', description);
    fd.append('area', area);
    if (file) fd.append('file', file);
    const res = await fetch(`${BASE}/api/basecamp/content-proposals`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd, // FormData — browser postavlja Content-Type sa boundary
    });
    if (!res.ok) { const e = await res.json().catch(() => ({ message: res.statusText })); throw new Error(e.message || `HTTP ${res.status}`); }
    return res.json();
  },
  getMyProposals: () => request('/api/basecamp/content-proposals/mine'),
  getProposalQueue: () => request('/api/basecamp/content-proposals/queue'),
  getApprovedContent: () => request('/api/basecamp/content-proposals/approved'),
  decideProposal: (id, decision, comment) => request(`/api/basecamp/content-proposals/${id}/decide`, { method: 'PUT', body: JSON.stringify({ decision, comment }) }),
  // Preuzimanje priloženog fajla (endpoint traži Bearer token → blob → download).
  downloadProposalFile: async (id, filename) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/basecamp/content-proposals/${id}/file`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error('File not available');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'content';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  // Community
  // all=true (samo superadmin) uključuje i osobe isključene iz Basecamp-a (isMember=false).
  getMembers: (all = false) => request(`/api/basecamp/members${all ? '?all=1' : ''}`),
  getMemberDetails: (id) => request(`/api/basecamp/members/${id}`),
  getBpNames: () => request('/api/basecamp/bp-names'),
  // CV preuzimanje — endpoint traži Bearer token → blob → download.
  downloadMemberCv: async (id, filename) => {
    const token = getToken();
    const res = await fetch(`${BASE}/api/basecamp/members/${id}/cv`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error('CV not available');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'cv';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  // Profilna slika iz Microsoft Graph — endpoint traži Bearer token (ne može ga
  // <img src>), pa je povlačimo kao blob i vraćamo object URL (ili null → inicijal).
  // Cached per session: one fetch per member, shared by every avatar that shows them.
  getMemberPhotoURL: (id) => {
    if (!_photoCache.has(id)) _photoCache.set(id, fetchMemberPhoto(id));
    return _photoCache.get(id);
  },

  // Grow → Observation & Co-delivery (model UC6): masters open days, Ice Pilots ask for a seat.
  observation: {
    list: () => request('/api/basecamp/observation'),
    openDay: (data) => request('/api/basecamp/observation/days', { method: 'POST', body: JSON.stringify(data) }),
    cancelDay: (id) => request(`/api/basecamp/observation/days/${id}`, { method: 'DELETE' }),
    requestSeat: (id, kind, message) => request(`/api/basecamp/observation/days/${id}/request`, { method: 'POST', body: JSON.stringify({ kind, message }) }),
    decideSeat: (seatId, action, hours) => request(`/api/basecamp/observation/seats/${seatId}`, { method: 'POST', body: JSON.stringify({ action, hours }) }),
  },

  // Chat — 1:1 poruke (port iz Compass Messages)
  chat: {
    people: () => request('/api/basecamp/chat/people'),
    conversations: () => request('/api/basecamp/chat/conversations'),
    unreadCount: () => request('/api/basecamp/chat/unread-count'),
    thread: (otherId) => request(`/api/basecamp/chat/${otherId}`),
    send: (otherId, body) => request(`/api/basecamp/chat/${otherId}`, { method: 'POST', body: JSON.stringify({ body }) }),
    markRead: (otherId) => request(`/api/basecamp/chat/${otherId}/read`, { method: 'POST' }),
  },

  // Forum — kategorije, objave, odgovori (port iz Compass Forum)
  forum: {
    categories: () => request('/api/basecamp/forum/categories'),
    proposeCategory: (name) => request('/api/basecamp/forum/categories', { method: 'POST', body: JSON.stringify({ name }) }),
    approveCategory: (id) => request(`/api/basecamp/forum/categories/${id}/approve`, { method: 'POST' }),
    rejectCategory: (id) => request(`/api/basecamp/forum/categories/${id}/reject`, { method: 'POST' }),
    renameCategory: (id, name) => request(`/api/basecamp/forum/categories/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
    reorderCategories: (orderedIds) => request('/api/basecamp/forum/categories/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),
    deleteCategory: (id) => request(`/api/basecamp/forum/categories/${id}`, { method: 'DELETE' }),
    posts: (categoryId) => request(`/api/basecamp/forum/posts${categoryId ? `?categoryId=${categoryId}` : ''}`),
    post: (id) => request(`/api/basecamp/forum/posts/${id}`),
    createPost: (data) => request('/api/basecamp/forum/posts', { method: 'POST', body: JSON.stringify(data) }),
    reply: (postId, body, parentReplyId = null) => request(`/api/basecamp/forum/posts/${postId}/replies`, { method: 'POST', body: JSON.stringify({ body, parentReplyId }) }),
    deletePost: (id) => request(`/api/basecamp/forum/posts/${id}`, { method: 'DELETE' }),
    deleteReply: (id) => request(`/api/basecamp/forum/replies/${id}`, { method: 'DELETE' }),
  },

  // Events (Faza 2)
  getEvents: () => request('/api/basecamp/events'),
  getEvent: (id) => request(`/api/basecamp/events/${id}`),
  createEvent: (data) => request('/api/basecamp/events', { method: 'POST', body: JSON.stringify(data) }),
  updateEvent: (id, data) => request(`/api/basecamp/events/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  toggleSignup: (id) => request(`/api/basecamp/events/${id}/signup`, { method: 'POST' }),
  toggleVote: (id) => request(`/api/basecamp/events/${id}/vote`, { method: 'POST' }),

  // Tasks (Faza 2)
  getTasks: () => request('/api/basecamp/tasks'),
  createTask: (title) => request('/api/basecamp/tasks', { method: 'POST', body: JSON.stringify({ title }) }),
  toggleTask: (id, done) => request(`/api/basecamp/tasks/${id}`, { method: 'PUT', body: JSON.stringify({ done }) }),
  deleteTask: (id) => request(`/api/basecamp/tasks/${id}`, { method: 'DELETE' }),

  // News & Calendar (Faza 2)
  getNews: () => request('/api/basecamp/news'),
  createNews: (data) => request('/api/basecamp/news', { method: 'POST', body: JSON.stringify(data) }),

  // Sales Process (WI-B1)
  getProcess: () => request('/api/basecamp/process'),
  getProcessVersions: () => request('/api/basecamp/process/versions'),
  getProcessEvents: () => request('/api/basecamp/process/events'),
  activateProcess: (id) => request(`/api/basecamp/process/${id}/activate`, { method: 'POST' }),
  addProcessStage: (processId, data) => request(`/api/basecamp/process/${processId}/stages`, { method: 'POST', body: JSON.stringify(data) }),
  updateProcessStage: (stageId, data) => request(`/api/basecamp/process/stages/${stageId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProcessStage: (stageId) => request(`/api/basecamp/process/stages/${stageId}`, { method: 'DELETE' }),
  reorderProcessStages: (processId, order) => request(`/api/basecamp/process/${processId}/stages/reorder`, { method: 'PUT', body: JSON.stringify({ order }) }),

  // The Songbook (WI-C6)
  getSongbook: () => request('/api/basecamp/songbook'),
  createIngredient: (data) => request('/api/basecamp/songbook', { method: 'POST', body: JSON.stringify(data) }),
  updateIngredient: (id, data) => request(`/api/basecamp/songbook/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteIngredient: (id) => request(`/api/basecamp/songbook/${id}`, { method: 'DELETE' }),
  packIngredient: (id) => request(`/api/basecamp/songbook/${id}/pack`, { method: 'POST' }),
  // UC7: share a personal note with the band (no permission) / take it back; custodians promote to Standards.
  shareIngredient: (id, undo = false) => request(`/api/basecamp/songbook/${id}/share`, { method: 'POST', body: JSON.stringify({ undo }) }),
  promoteIngredient: (id) => request(`/api/basecamp/songbook/${id}/promote`, { method: 'POST' }),

  // Explore (Faza 3A)
  explore: {
    getProjects: () => request('/api/basecamp/explore/projects'),
    createProject: (data) => request('/api/basecamp/explore/projects', { method: 'POST', body: JSON.stringify(data) }),
    updateProject: (id, data) => request(`/api/basecamp/explore/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    addParty: (id, data) => request(`/api/basecamp/explore/projects/${id}/parties`, { method: 'POST', body: JSON.stringify(data) }),
    decideGraduation: (id, decision) => request(`/api/basecamp/explore/projects/${id}/graduation`, { method: 'POST', body: JSON.stringify({ decision }) }),
    getClients: () => request('/api/basecamp/explore/clients'),
    getFunnel: () => request('/api/basecamp/explore/funnel'),
    // Find clients — fit heat map and the criteria profile (HB Sales Tool W3)
    findClients: (period = 0) => request(`/api/basecamp/explore/find-clients?period=${encodeURIComponent(period)}`),
    openFromFindClients: (data) => request('/api/basecamp/explore/find-clients/open', { method: 'POST', body: JSON.stringify(data) }),
    getFitProfile: () => request('/api/basecamp/explore/fit-profile'),
    saveFitProfile: (data) => request('/api/basecamp/explore/fit-profile', { method: 'PUT', body: JSON.stringify(data) }),
    resetFitProfile: () => request('/api/basecamp/explore/fit-profile', { method: 'DELETE' }),
    // Ownership & collaboration (WI-B5)
    getCompanyEngagement: (id) => request(`/api/basecamp/explore/companies/${id}/engagement`),
    getClientKnown: (clientId) => request(`/api/basecamp/explore/clients/${clientId}/known`),
    notifyCompany: (id) => request(`/api/basecamp/explore/companies/${id}/notify`, { method: 'POST' }),
    addCoSeller: (id, userId) => request(`/api/basecamp/explore/projects/${id}/cosellers`, { method: 'POST', body: JSON.stringify({ userId }) }),
    removeCoSeller: (id, userId) => request(`/api/basecamp/explore/projects/${id}/cosellers/${userId}`, { method: 'DELETE' }),
    handover: (id, newOwnerId) => request(`/api/basecamp/explore/projects/${id}/handover`, { method: 'POST', body: JSON.stringify({ newOwnerId }) }),
    // Documents, activities, reminders, import/export (WI-B7)
    getDocuments: (id) => request(`/api/basecamp/explore/projects/${id}/documents`),
    addDocument: (id, data) => request(`/api/basecamp/explore/projects/${id}/documents`, { method: 'POST', body: JSON.stringify(data) }),
    deleteDocument: (id, docId) => request(`/api/basecamp/explore/projects/${id}/documents/${docId}`, { method: 'DELETE' }),
    getActivities: (id) => request(`/api/basecamp/explore/projects/${id}/activities`),
    addActivity: (id, data) => request(`/api/basecamp/explore/projects/${id}/activities`, { method: 'POST', body: JSON.stringify(data) }),
    toggleActivity: (id, aid) => request(`/api/basecamp/explore/projects/${id}/activities/${aid}/toggle`, { method: 'POST' }),
    deleteActivity: (id, aid) => request(`/api/basecamp/explore/projects/${id}/activities/${aid}`, { method: 'DELETE' }),
    getMyWeek: () => request('/api/basecamp/explore/my-week'),
    importPreview: (rows) => request('/api/basecamp/explore/import/preview', { method: 'POST', body: JSON.stringify({ rows }) }),
    importCommit: (rows) => request('/api/basecamp/explore/import/commit', { method: 'POST', body: JSON.stringify({ rows }) }),
    exportCsv: () => request('/api/basecamp/explore/export'),
    getCompanies: (q = '') => request(`/api/basecamp/explore/companies${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    getCompany: (id) => request(`/api/basecamp/explore/companies/${id}`),
    // Stage rail (WI-B2)
    getRail: (id) => request(`/api/basecamp/explore/projects/${id}/rail`),
    toggleMilestone: (id, mid) => request(`/api/basecamp/explore/projects/${id}/milestones/${mid}`, { method: 'POST' }),
    // HB Way of Selling v1.1: step confirmations, outcome, territory day price.
    setStep: (id, code, data) => request(`/api/basecamp/explore/projects/${id}/steps/${code}`, { method: 'POST', body: JSON.stringify(data) }),
    setOutcome: (id, data) => request(`/api/basecamp/explore/projects/${id}/outcome`, { method: 'POST', body: JSON.stringify(data) }),
    getTerritorySettings: () => request('/api/basecamp/explore/territory-settings'),
    setTerritorySettings: (data) => request('/api/basecamp/explore/territory-settings', { method: 'PUT', body: JSON.stringify(data) }),
    // UC1: personal prospecting criteria + per-project fit.
    getCriteria: () => request('/api/basecamp/explore/criteria'),
    saveCriteria: (criteria) => request('/api/basecamp/explore/criteria', { method: 'PUT', body: JSON.stringify({ criteria }) }),
    getFit: (id) => request(`/api/basecamp/explore/projects/${id}/fit`),
    toggleFit: (id, cid) => request(`/api/basecamp/explore/projects/${id}/fit/${cid}`, { method: 'POST' }),
    // The stage is derived from events (no manual advance): Won → Delivering, delivered → Completed.
    markDelivered: (id, undo = false) => request(`/api/basecamp/explore/projects/${id}/delivered`, { method: 'POST', body: JSON.stringify({ undo }) }),
    markLost: (id, data) => request(`/api/basecamp/explore/projects/${id}/lost`, { method: 'POST', body: JSON.stringify(data) }),
    // HB Sales Tool W4: My clients, import, client page, facts and ✦ Sync.
    getMyClients: () => request('/api/basecamp/explore/my-clients'),
    addMyClient: (companyId) => request('/api/basecamp/explore/my-clients', { method: 'POST', body: JSON.stringify({ companyId }) }),
    removeMyClient: (companyId) => request(`/api/basecamp/explore/my-clients/${companyId}`, { method: 'DELETE' }),
    previewMyClientsImport: (text) => request('/api/basecamp/explore/my-clients/import/preview', { method: 'POST', body: JSON.stringify({ text }) }),
    importMyClients: (text) => request('/api/basecamp/explore/my-clients/import', { method: 'POST', body: JSON.stringify({ text }) }),
    getClientProfile: (id) => request(`/api/basecamp/explore/companies/${id}/profile`),
    getClientFacts: (id) => request(`/api/basecamp/explore/companies/${id}/facts`),
    getSyncPrompt: (id) => request(`/api/basecamp/explore/companies/${id}/sync-prompt`),
    runSync: (id) => request(`/api/basecamp/explore/companies/${id}/sync/run`, { method: 'POST' }),
    pasteSyncAnswer: (id, text) => request(`/api/basecamp/explore/companies/${id}/sync/answer`, { method: 'POST', body: JSON.stringify({ text }) }),
    decideFact: (factId, decision) => request(`/api/basecamp/explore/facts/${factId}`, { method: 'POST', body: JSON.stringify({ decision }) }),
    // W6: market signals of the territory (and the network).
    getSignals: () => request('/api/basecamp/explore/signals'),
    addSignal: (data) => request('/api/basecamp/explore/signals', { method: 'POST', body: JSON.stringify(data) }),
    updateSignal: (id, data) => request(`/api/basecamp/explore/signals/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    removeSignal: (id) => request(`/api/basecamp/explore/signals/${id}`, { method: 'DELETE' }),
  },

  // W6: translation of HB content (draft → shared → approved by the custodian).
  translations: {
    list: (type, key) => request(`/api/basecamp/translations?type=${encodeURIComponent(type)}&key=${encodeURIComponent(key)}`),
    create: (type, key, lang) => request('/api/basecamp/translations', { method: 'POST', body: JSON.stringify({ type, key, lang }) }),
    update: (id, data) => request(`/api/basecamp/translations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    share: (id) => request(`/api/basecamp/translations/${id}/share`, { method: 'POST' }),
    decide: (id, decision) => request(`/api/basecamp/translations/${id}/decide`, { method: 'POST', body: JSON.stringify({ decision }) }),
  },

  // W7: AI usage and cost per person and partner.
  usage: {
    me: () => request('/api/basecamp/usage/me'),
    month: (month) => request(`/api/basecamp/usage${month ? `?month=${encodeURIComponent(month)}` : ''}`),
    setLimit: (data) => request('/api/basecamp/usage/limits', { method: 'PUT', body: JSON.stringify(data) }),
    removeLimit: (scope, key) => request(`/api/basecamp/usage/limits/${encodeURIComponent(scope)}/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  },

  // AI capabilities + approvals (Faza 3C)
  ai: {
    intake: (projectId, notes) => request('/api/basecamp/ai/intake', { method: 'POST', body: JSON.stringify({ projectId, notes }) }),
    adaptUnit: (projectId, unitCode, context) => request('/api/basecamp/ai/adapt-unit', { method: 'POST', body: JSON.stringify({ projectId, unitCode, context }) }),
    draftUnit: (projectId, gaps, context) => request('/api/basecamp/ai/draft-unit', { method: 'POST', body: JSON.stringify({ projectId, gaps, context }) }),
    getApprovals: (queue) => request(`/api/basecamp/ai/approvals?queue=${queue || 'mine'}`),
    decideApproval: (id, decision, comment) => request(`/api/basecamp/ai/approvals/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision, comment }) }),
    // Hansen Beck assistant (read-only chat; paid → request → admin grants)
    assistantStatus: () => request('/api/basecamp/ai/assistant/status'),
    requestAssistant: (note) => request('/api/basecamp/ai/assistant/request', { method: 'POST', body: JSON.stringify({ note }) }),
    assistantRequests: () => request('/api/basecamp/ai/assistant/requests'),
    decideAssistant: (userId, decision) => request(`/api/basecamp/ai/assistant/requests/${userId}`, { method: 'POST', body: JSON.stringify({ decision }) }),
    assistantConversations: () => request('/api/basecamp/ai/assistant/conversations'),
    newAssistantConversation: () => request('/api/basecamp/ai/assistant/conversations', { method: 'POST' }),
    assistantMessages: (conversationId) => request(`/api/basecamp/ai/assistant/conversations/${conversationId}/messages`),
    renameAssistantConversation: (conversationId, title) => request(`/api/basecamp/ai/assistant/conversations/${conversationId}`, { method: 'PUT', body: JSON.stringify({ title }) }),
    deleteAssistantConversation: (conversationId) => request(`/api/basecamp/ai/assistant/conversations/${conversationId}`, { method: 'DELETE' }),
    assistantChat: (question, conversationId) => request('/api/basecamp/ai/assistant/chat', { method: 'POST', body: JSON.stringify({ question, conversationId }) }),
    assistantKnowledge: () => request('/api/basecamp/ai/assistant/knowledge'),
    rebuildAssistantKnowledge: () => request('/api/basecamp/ai/assistant/knowledge/rebuild', { method: 'POST' }),
  },

  // Deliver za klijenta (Faza 3C)
  deliver: {
    getMyClients: () => request('/api/basecamp/deliver/my-clients'),
    getDayMaterials: (deliveryId) => request(`/api/basecamp/deliver/deliveries/${deliveryId}/materials`),
    scheduleDay: (deliveryId, plannedDate) => request(`/api/basecamp/deliver/deliveries/${deliveryId}`, { method: 'PUT', body: JSON.stringify({ plannedDate }) }),
    // closeDay(id, "report") or closeDay(id, { report, materialsUsed, adaptation, shareArea }) — model UC5 "keep what lasts".
    closeDay: (deliveryId, data) => request(`/api/basecamp/deliver/deliveries/${deliveryId}/close`, { method: 'POST', body: JSON.stringify(typeof data === 'string' ? { report: data } : data) }),
    getProjectDeliveries: (projectId) => request(`/api/basecamp/deliver/projects/${projectId}/deliveries`),
  },

  // Master (Faza 3B)
  master: {
    getSignatureProfiles: () => request('/api/basecamp/master/signature-profiles'),
    searchFacets: (q = '', pillar = '') => request(`/api/basecamp/master/facets?q=${encodeURIComponent(q)}&pillar=${encodeURIComponent(pillar)}`),
    // M2 — Needs (role tracks, intake, synthesis, gates)
    getRoleTracks: (projectId) => request(`/api/basecamp/master/projects/${projectId}/role-tracks`),
    createRoleTrack: (projectId, data) => request(`/api/basecamp/master/projects/${projectId}/role-tracks`, { method: 'POST', body: JSON.stringify(data) }),
    setCompass: (trackId, useCompass) => request(`/api/basecamp/master/role-tracks/${trackId}/compass`, { method: 'PUT', body: JSON.stringify({ useCompass }) }),
    getRoleTrack: (id) => request(`/api/basecamp/master/role-tracks/${id}`),
    updateNeeds: (id, updates) => request(`/api/basecamp/master/role-tracks/${id}/needs`, { method: 'PUT', body: JSON.stringify(updates) }),
    addIntake: (id, data) => request(`/api/basecamp/master/role-tracks/${id}/intake`, { method: 'POST', body: JSON.stringify(data) }),
    deleteIntake: (id, sourceId) => request(`/api/basecamp/master/role-tracks/${id}/intake/${sourceId}`, { method: 'DELETE' }),
    uploadIntakeFile: async (id, file) => {
      const token = getToken();
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/api/basecamp/master/role-tracks/${id}/intake/file`, {
        method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({ message: res.statusText })); throw new Error(e.message || `HTTP ${res.status}`); }
      return res.json();
    },
    downloadIntakeFile: async (sourceId, filename) => {
      const token = getToken();
      const res = await fetch(`${BASE}/api/basecamp/master/intake/${sourceId}/file`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('File not available');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'intake';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    synthesiseNeeds: (id) => request(`/api/basecamp/master/role-tracks/${id}/needs/synthesise`, { method: 'POST' }),
    confirmGate: (id, step) => request(`/api/basecamp/master/role-tracks/${id}/gates/${step}`, { method: 'POST' }),
    reverseGate: (id, step) => request(`/api/basecamp/master/role-tracks/${id}/gates/${step}`, { method: 'DELETE' }),
    // M3 — Design (profile assembled from the library)
    proposeProfile: (trackId, baseProfile) => request(`/api/basecamp/master/role-tracks/${trackId}/profile/propose`, { method: 'POST', body: JSON.stringify({ baseProfile }) }),
    getTrackProfile: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/profile`),
    facetAction: (profileId, facetId, body) => request(`/api/basecamp/master/profiles/${profileId}/facet-items/${facetId}`, { method: 'PUT', body: JSON.stringify(body) }),
    addTension: (profileId, data) => request(`/api/basecamp/master/profiles/${profileId}/tensions`, { method: 'POST', body: JSON.stringify(data) }),
    deleteTension: (profileId, tensionId) => request(`/api/basecamp/master/profiles/${profileId}/tensions/${tensionId}`, { method: 'DELETE' }),
    exportProfile: (profileId) => request(`/api/basecamp/master/profiles/${profileId}/export`, { method: 'POST' }),
    proposeFacet: (data) => request('/api/basecamp/master/facets/propose', { method: 'POST', body: JSON.stringify(data) }),
    // M4 — Programme (day grid)
    composeProgramme: (trackId, days) => request(`/api/basecamp/master/role-tracks/${trackId}/programme/compose`, { method: 'POST', body: JSON.stringify({ days }) }),
    // Route B (no HB Compass): Units suggested from the written need; compose from chosen Units.
    unitSuggestions: (trackId, q = '') => request(`/api/basecamp/master/role-tracks/${trackId}/unit-suggestions${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    composeProgrammeDirect: (trackId, days, unitCodes) => request(`/api/basecamp/master/role-tracks/${trackId}/programme/compose-direct`, { method: 'POST', body: JSON.stringify({ days, unitCodes }) }),
    getTrackProgramme: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/programme`),
    // W5 — Design in the customer workspace (shape · adapt · new, Gate 2 writes the commercials)
    getDesign: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/design`),
    startDesign: (trackId, slug) => request(`/api/basecamp/master/role-tracks/${trackId}/design/start`, { method: 'POST', body: JSON.stringify({ slug }) }),
    shapeDesign: (trackId, data) => request(`/api/basecamp/master/role-tracks/${trackId}/design`, { method: 'PUT', body: JSON.stringify(data) }),
    addDesignDay: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/design/days`, { method: 'POST' }),
    addDesignSlot: (trackId, data) => request(`/api/basecamp/master/role-tracks/${trackId}/design/slots`, { method: 'POST', body: JSON.stringify(data) }),
    updateDesignSlot: (trackId, slotId, data) => request(`/api/basecamp/master/role-tracks/${trackId}/design/slots/${slotId}`, { method: 'PUT', body: JSON.stringify(data) }),
    removeDesignSlot: (trackId, slotId) => request(`/api/basecamp/master/role-tracks/${trackId}/design/slots/${slotId}`, { method: 'DELETE' }),
    resetDesign: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/design/reset`, { method: 'POST' }),
    // M5 — Proposal
    initProposal: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/proposal/init`, { method: 'POST' }),
    getTrackProposal: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/proposal`),
    updateCommercials: (proposalId, data) => request(`/api/basecamp/master/proposals/${proposalId}/commercials`, { method: 'PUT', body: JSON.stringify(data) }),
    artefactAction: (artefactId, body) => request(`/api/basecamp/master/artefacts/${artefactId}`, { method: 'PUT', body: JSON.stringify(body) }),
    // M6 — Forms
    sendForm: (trackId, template, partyIds) => request(`/api/basecamp/master/role-tracks/${trackId}/forms`, { method: 'POST', body: JSON.stringify({ template, partyIds }) }),
    getForms: (trackId) => request(`/api/basecamp/master/role-tracks/${trackId}/forms`),
    getEngagement: (projectId) => request(`/api/basecamp/master/projects/${projectId}/engagement`),
    createProfile: (projectId, data) => request(`/api/basecamp/master/projects/${projectId}/profile`, { method: 'POST', body: JSON.stringify(data) }),
    updateFacets: (profileId, updates) => request(`/api/basecamp/master/profiles/${profileId}/facets`, { method: 'PUT', body: JSON.stringify({ updates }) }),
    reviewDimension: (profileId, dimension, reviewed) => request(`/api/basecamp/master/profiles/${profileId}/review`, { method: 'PUT', body: JSON.stringify({ dimension, reviewed }) }),
    approveProfile: (profileId, reviewAll) => request(`/api/basecamp/master/profiles/${profileId}/approve`, { method: 'POST', body: JSON.stringify({ reviewAll: !!reviewAll }) }),
    getMatching: (profileId) => request(`/api/basecamp/master/profiles/${profileId}/matching`),
    saveProgramme: (projectId, data) => request(`/api/basecamp/master/projects/${projectId}/programme`, { method: 'POST', body: JSON.stringify(data) }),
    createProposal: (projectId, notes) => request(`/api/basecamp/master/projects/${projectId}/proposals`, { method: 'POST', body: JSON.stringify({ notes }) }),
    updateProposal: (proposalId, status) => request(`/api/basecamp/master/proposals/${proposalId}`, { method: 'PUT', body: JSON.stringify({ status }) }),
    updateProposalDetails: (proposalId, data) => request(`/api/basecamp/master/proposals/${proposalId}/details`, { method: 'PUT', body: JSON.stringify(data) }),
    generateProposalDoc: (proposalId) => request(`/api/basecamp/master/proposals/${proposalId}/generate-document`, { method: 'POST' }),
    closeEngagement: (projectId) => request(`/api/basecamp/master/projects/${projectId}/close`, { method: 'POST' }),
  },

  // CFA pipeline (Faza 2)
  cfa: {
    getCandidates: () => request('/api/basecamp/cfa/candidates'),
    getCandidate: (id) => request(`/api/basecamp/cfa/candidates/${id}`),
    createCandidate: (data) => request('/api/basecamp/cfa/candidates', { method: 'POST', body: JSON.stringify(data) }),
    advanceCandidate: (id) => request(`/api/basecamp/cfa/candidates/${id}/advance`, { method: 'POST' }),
    updateCandidate: (id, data) => request(`/api/basecamp/cfa/candidates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getMyJourney: () => request('/api/basecamp/cfa/my-journey'),
    getClasses: () => request('/api/basecamp/cfa/classes'),
    updateSession: (id, data) => request(`/api/basecamp/cfa/sessions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getFunnel: () => request('/api/basecamp/cfa/funnel'),
  },
};
